import ROUTES from 'constants/routes';
import { readGlobalTimeRange } from 'utils/globalTimeStorageUtils';
import { TFunction } from 'i18next';

import { CustomTimeType, Option, Time, TimeFrame } from './types';

export const DEFAULT_TIME_RANGE = '30m';

export const Options: Option[] = [
	{ value: '5m', label: 'Last 5 minutes' },
	{ value: '15m', label: 'Last 15 minutes' },
	{ value: '30m', label: 'Last 30 minutes' },
	{ value: '1h', label: 'Last 1 hour' },
	{ value: '6h', label: 'Last 6 hours' },
	{ value: '1d', label: 'Last 1 day' },
	{ value: '3d', label: 'Last 3 days' },
	{ value: '1w', label: 'Last 1 week' },
	{ value: '1month', label: 'Last 1 month' },
	{ value: 'custom', label: 'Custom Date Range' },
];

export const RelativeTimeMap: TimeFrame = {
	'5min': '5m',
	'15min': '15m',
	'30min': '30m',
	'1hr': '1h',
	'6hr': '6h',
	'1day': '1d',
	'3days': '3d',
	'1week': '1w',
};

export const OLD_RELATIVE_TIME_VALUES = [
	'1min',
	'10min',
	'15min',
	'1hr',
	'30min',
	'45min',
	'5min',
	'1day',
	'3days',
	'4days',
	'10days',
	'1week',
	'2weeks',
	'6weeks',
	'3hr',
	'4hr',
	'6hr',
	'12hr',
];

export const RelativeDurationOptions: Option[] = [
	{ value: '5m', label: 'Last 5 minutes' },
	{ value: '15m', label: 'Last 15 minutes' },
	{ value: '30m', label: 'Last 30 minutes' },
	{ value: '1h', label: 'Last 1 hour' },
	{ value: '6h', label: 'Last 6 hour' },
	{ value: '1d', label: 'Last 1 day' },
	{ value: '3d', label: 'Last 3 days' },
	{ value: '1w', label: 'Last 1 week' },
	{ value: '1month', label: 'Last 1 month' },
];

export const RelativeDurationSuggestionOptions: Option[] = [
	{ value: '3h', label: 'Last 3 hours' },
	{ value: '4d', label: 'Last 4 days' },
	{ value: '6w', label: 'Last 6 weeks' },
	{ value: '12h', label: 'Last 12 hours' },
	{ value: '10d', label: 'Last 10 days' },
	{ value: '2w', label: 'Last 2 weeks' },
	{ value: '2months', label: 'Last 2 months' },
	{ value: '1d', label: 'today' },
];
export const FixedDurationSuggestionOptions: Option[] = [
	{ value: '45m', label: 'Last 45 mins' },
	{ value: '12h', label: 'Last 12 hours' },
	{ value: '10d', label: 'Last 10 days' },
	{ value: '2w', label: 'Last 2 weeks' },
	{ value: '2months', label: 'Last 2 months' },
	{ value: '1d', label: 'today' },
];

// Maps the English option labels above to i18n keys in the `common` namespace.
// Option values stay English everywhere (URL params, redux state, lookups);
// labels are translated only at display time via translateTimeRangeLabel.
const TIME_RANGE_LABEL_I18N_KEYS: Record<string, string> = {
	'Last 5 minutes': 'time_range.last_5_minutes',
	'Last 15 minutes': 'time_range.last_15_minutes',
	'Last 30 minutes': 'time_range.last_30_minutes',
	'Last 45 mins': 'time_range.last_45_mins',
	'Last 1 hour': 'time_range.last_1_hour',
	'Last 3 hours': 'time_range.last_3_hours',
	'Last 6 hours': 'time_range.last_6_hours',
	'Last 6 hour': 'time_range.last_6_hours',
	'Last 12 hours': 'time_range.last_12_hours',
	'Last 1 day': 'time_range.last_1_day',
	'Last 3 days': 'time_range.last_3_days',
	'Last 4 days': 'time_range.last_4_days',
	'Last 10 days': 'time_range.last_10_days',
	'Last 1 week': 'time_range.last_1_week',
	'Last 2 weeks': 'time_range.last_2_weeks',
	'Last 6 weeks': 'time_range.last_6_weeks',
	'Last 1 month': 'time_range.last_1_month',
	'Last 2 months': 'time_range.last_2_months',
	today: 'time_range.today',
	'Custom Date Range': 'time_range.custom_date_range',
};

export const translateTimeRangeLabel = (
	t: TFunction,
	label: string,
): string => {
	const key = TIME_RANGE_LABEL_I18N_KEYS[label];
	return key ? t(key).toString() : label;
};

export const convertOldTimeToNewValidCustomTimeFormat = (
	time: string,
): CustomTimeType => {
	const regex = /^(\d+)([a-zA-Z]+)/;
	const match = regex.exec(time);

	if (match) {
		let unit = 'm';

		switch (match[2]) {
			case 'min':
				unit = 'm';
				break;
			case 'hr':
				unit = 'h';
				break;
			case 'day':
			case 'days':
				unit = 'd';
				break;
			case 'week':
			case 'weeks':
				unit = 'w';
				break;

			default:
				break;
		}

		return `${match[1]}${unit}` as CustomTimeType;
	}

	return DEFAULT_TIME_RANGE;
};

export const getDefaultOption = (route: string): Time => {
	if (route === ROUTES.SERVICE_MAP) {
		return RelativeDurationOptions[2].value;
	}
	if (route === ROUTES.APPLICATION) {
		return Options[2].value;
	}

	if (route === ROUTES.METER) {
		return Options[5].value;
	}
	return Options[2].value;
};

export const getOptions = (routes: string): Option[] => {
	if (routes === ROUTES.SERVICE_MAP) {
		return RelativeDurationOptions;
	}
	return Options;
};

/**
 * 절대 구간(custom)을 고를 수 있는 페이지인지. 서비스맵처럼 상대 범위만
 * 제공하는 페이지가 있어서, 전역 시간 범위가 절대 구간일 때 이 페이지들은
 * 자기 기본값으로 되돌려 보여 준다(저장된 전역 값은 건드리지 않는다).
 */
export const supportsCustomTimeRange = (route: string): boolean =>
	getOptions(route).some((option) => option.value === 'custom');

/**
 * 이 라우트에서 보여 줄 시간 범위를 정한다.
 *
 * 시간 범위는 앱 전체가 하나를 공유하므로, 마지막으로 고른 값이 있으면
 * 페이지 기본값보다 우선한다. 페이지 기본값은 그 브라우저에서 시간을 한 번도
 * 고른 적이 없을 때만 쓰인다. (URL에 실려 온 시간이 이 결과보다 우선하는 건
 * 호출하는 쪽에서 처리한다.)
 */
export const resolveTimeForRoute = (route: string): Time => {
	const defaultSelectedOption = getDefaultOption(route);
	const { preset, absolute } = readGlobalTimeRange();

	if (absolute) {
		// 서비스맵처럼 절대 구간을 못 그리는 페이지는 저장된 전역 값을 건드리지
		// 않고 표시만 그 페이지 기본값으로 되돌린다. 다른 페이지로 나가면 원래
		// 구간이 그대로 복원된다.
		return supportsCustomTimeRange(route) ? 'custom' : defaultSelectedOption;
	}

	if (preset) {
		return preset as Time;
	}

	return defaultSelectedOption;
};

export const routesToHideBreadCrumbs = [ROUTES.SUPPORT, ROUTES.ALL_DASHBOARD];

export const routesToSkip = [
	ROUTES.HOME,
	ROUTES.SETTINGS,
	ROUTES.LIST_ALL_ALERT,
	ROUTES.TRACE_DETAIL,
	ROUTES.ALL_CHANNELS,
	ROUTES.USAGE_EXPLORER,
	ROUTES.GET_STARTED,
	ROUTES.GET_STARTED_WITH_CLOUD,
	ROUTES.GET_STARTED_APPLICATION_MONITORING,
	ROUTES.GET_STARTED_INFRASTRUCTURE_MONITORING,
	ROUTES.GET_STARTED_LOGS_MANAGEMENT,
	ROUTES.GET_STARTED_AWS_MONITORING,
	ROUTES.GET_STARTED_AZURE_MONITORING,
	ROUTES.VERSION,
	ROUTES.ALL_DASHBOARD,
	ROUTES.ORG_SETTINGS,
	ROUTES.MEMBERS_SETTINGS,
	ROUTES.SERVICE_ACCOUNTS_SETTINGS,
	ROUTES.MCP_SERVER,
	ROUTES.AI_MODULE_SETTINGS,
	ROUTES.PII_PATTERN_SETTINGS,
	ROUTES.CODE_RCA_SETTINGS,
	ROUTES.REMEDIATION_TARGETS_SETTINGS,
	ROUTES.INCIDENT_REPORT_SETTINGS,
	ROUTES.WEEKLY_REPORT_SETTINGS,
	ROUTES.SOP_DOCUMENTS_SETTINGS,
	ROUTES.INGESTION_SETTINGS,
	ROUTES.ERROR_DETAIL,
	ROUTES.LOGS_PIPELINES,
	ROUTES.BILLING,
	ROUTES.ROLES_SETTINGS,
	ROUTES.ROLE_DETAILS,
	ROUTES.SUPPORT,
	ROUTES.WORKSPACE_LOCKED,
	ROUTES.WORKSPACE_SUSPENDED,
	ROUTES.LOGS,
	ROUTES.MY_SETTINGS,
	ROUTES.LIST_LICENSES,
	ROUTES.LOGS_SAVE_VIEWS,
	ROUTES.LOGS_PIPELINES,
	ROUTES.TRACES_EXPLORER,
	ROUTES.TRACES_SAVE_VIEWS,
	ROUTES.TRACES_FUNNELS,
	ROUTES.TRACES_FUNNELS_DETAIL,
	ROUTES.SHORTCUTS,
	ROUTES.INTEGRATIONS,
	ROUTES.INTEGRATIONS_DETAIL,
	ROUTES.DASHBOARD,
	ROUTES.DASHBOARD_WIDGET,
	ROUTES.SERVICE_TOP_LEVEL_OPERATIONS,
	ROUTES.ALERT_HISTORY,
	ROUTES.ALERT_OVERVIEW,
	ROUTES.MESSAGING_QUEUES_KAFKA,
	ROUTES.MESSAGING_QUEUES_KAFKA_DETAIL,
	ROUTES.MESSAGING_QUEUES_CELERY_TASK,
	ROUTES.MESSAGING_QUEUES_OVERVIEW,
	ROUTES.INFRASTRUCTURE_MONITORING_HOSTS,
	ROUTES.SOMETHING_WENT_WRONG,
	ROUTES.INFRASTRUCTURE_MONITORING_KUBERNETES,
	ROUTES.METRICS_EXPLORER,
	ROUTES.METRICS_EXPLORER_EXPLORER,
	ROUTES.METRICS_EXPLORER_VIEWS,
	ROUTES.API_MONITORING,
	ROUTES.CHANNELS_NEW,
	ROUTES.CHANNELS_EDIT,
	ROUTES.WORKSPACE_ACCESS_RESTRICTED,
	ROUTES.ALL_ERROR,
	ROUTES.UN_AUTHORIZED,
	ROUTES.NOT_FOUND,
	ROUTES.METER_EXPLORER,
	ROUTES.METER,
	ROUTES.METER_EXPLORER_VIEWS,
	ROUTES.SOMETHING_WENT_WRONG,
];

export const routesToDisable = [ROUTES.LOGS_EXPLORER, ROUTES.LIVE_LOGS];
